<template>
  <LoadingIndicator v-if="!loaded" />
  <SimplePage v-else :fixed-height="!!id">
    <template #heading>
      <StoreInfoBar />
    </template>
    <h1 class="title">予約コース一覧</h1>
    <div class="courses">
      <div v-for="course in allCourses" :key="course.id" class="course">
        <GnaviCourseInfo class="info" :course="course" large />
        <CalendarBar class="calendar" :holidays="allHolidays">
          <template #date="{ date, label, index }">
            <div class="date" @click="course.calendar[index].status === 1 && toGaReserve(course, date)">
              <div>{{ label }}</div>
              <ReceptionStatus
                class="status"
                :status="course.calendar[index].status"
                :not-available-type="setting.rsvNotAvailableType"
                :tel="setting.store.tel"
                @tel="event('daicho_list_tel')"
              />
            </div>
          </template>
        </CalendarBar>
        <div class="buttons">
          <Button primary class="button toReserve" @click="toGaReserve(course)">{{ $t('reserve') }}</Button>
          <Button class="button toDetail" @click="toDetail(course.id)">コース詳細</Button>
        </div>
      </div>
      <LoadingIndicator v-if="busy" inline />
    </div>
    <ScrollTop />
    <GnaviCourseModal v-if="id" :id="id" @reserve="toReserve" @close="toList" />
  </SimplePage>
</template>

<script>
import { throttle } from 'lodash'
import { mapState, mapGetters } from 'vuex'
import { event } from '@/utils/analytics'
import SimplePage from '@/components/templates/SimplePage'
import GnaviCourseModal from '@/components/organisms/GnaviCourseModal'
import StoreInfoBar from '@/components/organisms/StoreInfoBar'
import CalendarBar from '@/components/molecules/CalendarBar'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import ReceptionStatus from '@/components/molecules/ReceptionStatus'
import GnaviCourseInfo from '@/components/molecules/GnaviCourseInfo'
import Button from '@/components/atoms/Button'
import ScrollTop from '@/components/molecules/ScrollTop'

export default {
  name: 'Courses',
  components: {
    SimplePage,
    GnaviCourseModal,
    StoreInfoBar,
    CalendarBar,
    LoadingIndicator,
    ReceptionStatus,
    GnaviCourseInfo,
    Button,
    ScrollTop,
  },
  props: {
    id: { type: Number, default: null },
  },
  data() {
    return {
      loaded: false,
    }
  },
  computed: {
    ...mapState({
      setting: state => state.reception.setting,
      reception: state => state.reception,
      gnaviCourses: state => state.gnaviCourses,
      busy: state => state.gnaviCourses.busy,
    }),
    ...mapGetters({
      allCourses: 'gnaviCourses/all',
      allHolidays: 'holidays/all',
    }),
  },
  async mounted() {
    // ぐるなびコース利用でない場合は予約フォームへ遷移
    if (!this.reception.setting.useGnaviCourse) this.$router.replace({ name: 'reception' })

    this.$store.commit('gnaviCourses/clear')
    await this.$store.dispatch('gnaviCourses/get')
    // ぐるなびコースがない場合も予約フォームへ遷移
    if (!this.allCourses.length) this.$router.replace({ name: 'reception' })
    if (this.id && !this.gnaviCourses.byId[this.id]) await this.$store.dispatch('gnaviCourses/get', this.id)

    this.loaded = true

    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
  },
  methods: {
    event,
    onScroll: throttle(function() {
      if (this.busy) return
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop
      const height = document.body.scrollHeight - document.body.clientHeight * 2
      if (height < scrollTop) this.$store.dispatch('gnaviCourses/get')
    }, 200),
    toList() {
      this.$router.push({ name: 'courses' })
    },
    toDetail(id) {
      this.$router.push({ name: 'courseDetail', params: { id } })
    },
    toGaReserve(course, date) {
      this.event(date ? 'daicho_list_cal' : 'daicho_list_01')
      this.toReserve(course, date)
    },
    toReserve(course, date) {
      this.$store.commit('reservation/edit/update', { courses: [course], date })
      this.$router.push({ name: 'reception' })
    },
    hasDiscounted(course) {
      return course.price.discounted || course.price.discountedTaxIn
    },
    onlySeat(kind) {
      return !!kind && kind === 6
    },
  },
}
</script>

<style lang="postcss" scoped>
/* autoprefixer grid: autoplace */
.title {
  font-size: 14px;
  margin-bottom: 16px;

  @media screen and (min-width: 768px) {
    font-size: 20px;
  }
}

.courses {
  display: flex;
  flex-direction: column;
  margin: 0 -16px;
  @media screen and (min-width: 768px) {
    margin: 0;
  }
}

.course {
  background: var(--white);
  display: grid;
  grid-gap: 16px;
  grid-template:
    'info' auto
    'calendar' auto
    'buttons ' auto / 1fr;
  padding: 16px;

  & + .course {
    margin-top: 16px;
  }

  @media screen and (min-width: 768px) {
    grid-gap: 16px;
    grid-template:
      'info     buttons' max-content
      'calendar calendar' auto / 1fr 128px;
    padding: 24px 16px;
  }

  @media all and (-ms-high-contrast: none) {
    min-height: 0%;
  }
}

.info {
  grid-area: info;
}

.calendar {
  grid-area: calendar;
  margin: -8px -16px 0 0;
  @media screen and (min-width: 768px) {
    margin: 0;
  }
}

.date {
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  height: 48px;
  justify-content: space-between;
  line-height: 1.2;
  padding: 8px 0;
  width: 100%;

  @media screen and (min-width: 768px) {
    font-size: 14px;
    height: 68px;
    padding: 14px 0;
  }
}

.status {
  color: var(--text-color);
  font-size: 12px;
  height: 14px;

  & >>> svg {
    height: 14px;
    width: 14px;
  }
}

.buttons {
  display: grid;
  grid-area: buttons;
  grid-gap: 8px;
  grid-template-columns: repeat(2, auto);
  grid-template-rows: 44px;

  @media screen and (min-width: 768px) {
    grid-gap: 16px;
    grid-template-columns: 1fr;
    grid-template-rows: 40px 40px;
  }
}

.button {
  font-size: 14px;
  height: auto;
  width: auto;
}

.toReserve {
  order: 2;
  @media screen and (min-width: 768px) {
    order: 0;
  }
}
.toDetail {
  order: 1;
  @media screen and (min-width: 768px) {
    order: 0;
  }
}
</style>
